import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { modulesApi } from '../../../api/endpoints';
import { useAuthStore } from '../../../stores/auth.store';
import DataTable from '../../../components/common/DataTable';
import Modal from '../../../components/common/Modal';
import { formatCurrency } from '../../../lib/currency';
import type { Rental } from '@motra/shared';

export default function RentalPage() {
  const { t } = useTranslation();
  const shop = useAuthStore((s) => s.shop);
  const currSym = shop?.curr_sym ?? '$';
  const queryClient = useQueryClient();

  const { data: res, isLoading } = useQuery({
    queryKey: ['rentals', shop?.id],
    queryFn: () => modulesApi.listRentals(shop!.id),
    enabled: !!shop?.id,
  });

  const rentals = res?.data ?? [];

  const createRental = useMutation({
    mutationFn: (data: Partial<Rental> & { shopid: string }) =>
      modulesApi.createRental(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rentals'] }),
  });

  const updateRental = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Rental> }) =>
      modulesApi.updateRental(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rentals'] }),
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<Partial<Rental>>({
    rate_unit: 'day',
    status: 'active',
  });
  const [formError, setFormError] = useState('');

  function setField<K extends keyof Rental>(key: K, value: Rental[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!shop?.id) return;
    setFormError('');

    try {
      await createRental.mutateAsync({ ...form, shopid: shop.id });
      setModalOpen(false);
      setForm({ rate_unit: 'day', status: 'active' });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Create failed');
    }
  }

  async function handleReturn(rental: Rental) {
    await updateRental.mutateAsync({
      id: rental.id,
      data: {
        status: 'returned',
        returned_datetime: new Date().toISOString(),
      },
    });
  }

  const columns = [
    { header: 'Item', accessor: 'item_name' as keyof Rental },
    { header: 'Customer', accessor: 'customer_id' as keyof Rental },
    {
      header: 'Rate',
      accessor: 'rate' as keyof Rental,
      render: (val: unknown, row: Rental) =>
        `${formatCurrency(Number(val) || 0, currSym)}/${row.rate_unit ?? 'day'}`,
    },
    {
      header: 'Start',
      accessor: 'start_datetime' as keyof Rental,
      render: (val: unknown) =>
        val ? new Date(String(val)).toLocaleDateString() : '—',
    },
    {
      header: 'End',
      accessor: 'end_datetime' as keyof Rental,
      render: (val: unknown) =>
        val ? new Date(String(val)).toLocaleDateString() : '—',
    },
    {
      header: t('status', 'Status'),
      accessor: 'status' as keyof Rental,
      render: (val: unknown) => (
        <span
          className={`badge badge--${
            val === 'active' ? 'success' : val === 'overdue' ? 'danger' : 'neutral'
          }`}
        >
          {String(val ?? '—')}
        </span>
      ),
    },
    {
      header: 'Actions',
      accessor: 'id' as keyof Rental,
      render: (_v: unknown, row: Rental) => (
        row.status === 'active' ? (
          <button
            className="btn btn--sm btn--primary"
            onClick={(e) => { e.stopPropagation(); handleReturn(row); }}
            disabled={updateRental.isPending}
          >
            Return
          </button>
        ) : null
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">{t('rental', 'Rental')}</h1>
        <button className="btn btn--primary" onClick={() => setModalOpen(true)}>
          + New Rental
        </button>
      </div>

      <DataTable
        columns={columns}
        data={rentals}
        loading={isLoading}
        rowKey={(r) => r.id}
        emptyMessage="No active rentals"
      />

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="New Rental"
        size="md"
      >
        <form onSubmit={handleCreate}>
          <div className="form-group">
            <label className="form-label">Item Name *</label>
            <input
              type="text"
              className="form-input"
              value={form.item_name ?? ''}
              onChange={(e) => setField('item_name', e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Customer ID</label>
            <input
              type="text"
              className="form-input"
              value={form.customer_id ?? ''}
              onChange={(e) => setField('customer_id', e.target.value)}
            />
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Rate</label>
              <input
                type="number"
                className="form-input"
                value={form.rate ?? ''}
                onChange={(e) => setField('rate', parseFloat(e.target.value) || 0)}
                step="0.01"
                min={0}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Rate Unit</label>
              <select
                className="form-select"
                value={form.rate_unit ?? 'day'}
                onChange={(e) => setField('rate_unit', e.target.value)}
              >
                <option value="hour">Hour</option>
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
              </select>
            </div>
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Start Date</label>
              <input
                type="datetime-local"
                className="form-input"
                value={form.start_datetime ?? ''}
                onChange={(e) => setField('start_datetime', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">End Date</label>
              <input
                type="datetime-local"
                className="form-input"
                value={form.end_datetime ?? ''}
                onChange={(e) => setField('end_datetime', e.target.value)}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Deposit</label>
            <input
              type="number"
              className="form-input"
              value={form.deposit ?? ''}
              onChange={(e) => setField('deposit', parseFloat(e.target.value) || 0)}
              step="0.01"
              min={0}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              className="form-textarea"
              value={form.note ?? ''}
              onChange={(e) => setField('note', e.target.value)}
              rows={2}
            />
          </div>

          {formError && <div className="alert alert--error">{formError}</div>}

          <div className="form-actions">
            <button type="button" className="btn btn--outline" onClick={() => setModalOpen(false)}>
              {t('cancel', 'Cancel')}
            </button>
            <button type="submit" className="btn btn--primary" disabled={createRental.isPending}>
              {createRental.isPending ? 'Creating...' : 'Create Rental'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
